import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { BarChart3, TrendingUp, TrendingDown, Mail, Package, Star, Clock, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getAnalytics, getDashboardStats } from '../../services/api';

const COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#f97316', '#ef4444', '#84cc16'];
const STATUS_COLORS = { new: '#3b82f6', contacted: '#f59e0b', converted: '#10b981', closed: '#6b7280' };

const MiniBar = ({ data, maxVal, color = '#3b82f6', height = 48 }) => {
  const max = maxVal || Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-[2px]" style={{ height }} data-testid="enquiry-trend-chart">
      {data.map((d, i) => (
        <div key={i} className="flex-1 group relative">
          <div
            className="w-full rounded-t-sm transition-all duration-200 group-hover:opacity-80"
            style={{ height: `${Math.max((d.count / max) * 100, 2)}%`, backgroundColor: color, minHeight: 2 }}
          />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
            <div className="bg-gray-900 text-white text-[10px] px-2 py-1 rounded shadow whitespace-nowrap">
              {d.date}: {d.count}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const HorizontalBar = ({ items, maxVal }) => {
  const max = maxVal || Math.max(...items.map(i => i.count), 1);
  return (
    <div className="space-y-2.5">
      {items.map((item, i) => (
        <div key={i} className="group">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600 truncate max-w-[60%]">{item.name}</span>
            <span className="text-xs font-semibold text-gray-900">{item.count}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(item.count / max) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const AnalyticsAdmin = () => {
  const { user } = useOutletContext();
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [aRes, sRes] = await Promise.all([getAnalytics(days), getDashboardStats()]);
        setAnalytics(aRes.data);
        setStats(sRes.data);
      } catch (e) { console.error('Analytics fetch failed', e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [days]);

  const monthChange = useMemo(() => {
    if (!analytics?.monthly_comparison) return null;
    const { this_month, last_month } = analytics.monthly_comparison;
    if (last_month === 0) return this_month > 0 ? 100 : 0;
    return Math.round(((this_month - last_month) / last_month) * 100);
  }, [analytics]);

  const totalEnquiriesInPeriod = useMemo(() => {
    if (!analytics?.enquiry_trend) return 0;
    return analytics.enquiry_trend.reduce((s, d) => s + d.count, 0);
  }, [analytics]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="analytics-page">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-200 rounded-xl animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-64 bg-gray-200 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const summaryCards = [
    {
      label: 'Total Enquiries', value: stats?.total_enquiries || 0,
      sub: `${stats?.pending_enquiries || 0} pending`,
      icon: Mail, color: 'bg-blue-500', trend: monthChange,
    },
    {
      label: 'Products', value: stats?.total_products || 0,
      sub: `${stats?.total_categories || 0} categories`,
      icon: Package, color: 'bg-indigo-500',
    },
    {
      label: 'Reviews', value: analytics?.reviews?.total || 0,
      sub: `${analytics?.reviews?.approved || 0} approved`,
      icon: Star, color: 'bg-amber-500',
    },
    {
      label: 'This Month', value: analytics?.monthly_comparison?.this_month || 0,
      sub: `vs ${analytics?.monthly_comparison?.last_month || 0} last month`,
      icon: TrendingUp, color: 'bg-emerald-500', trend: monthChange,
    },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500">Business insights and trends</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map(d => (
            <Button
              key={d} size="sm" variant={days === d ? 'default' : 'outline'}
              onClick={() => setDays(d)}
              className={days === d ? 'bg-[#2E3A6F] hover:bg-[#1a1f3a] text-white' : 'text-gray-600'}
              data-testid={`analytics-period-${d}`}
            >
              {d === 7 ? '7D' : d === 30 ? '30D' : '90D'}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card, i) => (
          <Card key={i} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`analytics-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{card.value.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center flex-shrink-0`}>
                  <card.icon className="text-white" size={20} />
                </div>
              </div>
              {card.trend !== undefined && card.trend !== null && (
                <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${card.trend > 0 ? 'text-emerald-600' : card.trend < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                  {card.trend > 0 ? <ArrowUpRight size={14} /> : card.trend < 0 ? <ArrowDownRight size={14} /> : <Minus size={14} />}
                  {Math.abs(card.trend)}% vs last month
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Enquiry Trend + Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 size={18} className="text-[#2E3A6F]" />
                Enquiry Trend
              </h3>
              <span className="text-sm text-gray-500">{totalEnquiriesInPeriod} enquiries in {days} days</span>
            </div>
            {analytics?.enquiry_trend?.length > 0 ? (
              <MiniBar data={analytics.enquiry_trend} height={140} color="#3b82f6" />
            ) : (
              <div className="h-36 flex items-center justify-center text-gray-400 text-sm">No data</div>
            )}
            <div className="flex justify-between mt-2 text-[10px] text-gray-400">
              <span>{analytics?.enquiry_trend?.[0]?.date}</span>
              <span>{analytics?.enquiry_trend?.[analytics.enquiry_trend.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock size={18} className="text-[#2E3A6F]" />
              Enquiry Status
            </h3>
            {analytics?.enquiry_statuses && Object.keys(analytics.enquiry_statuses).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(analytics.enquiry_statuses).sort((a, b) => b[1] - a[1]).map(([status, count], i) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] || COLORS[i % COLORS.length] }} />
                      <span className="text-sm text-gray-600 capitalize">{status}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-36 flex items-center justify-center text-gray-400 text-sm">No data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category Products + Top Enquired Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Package size={18} className="text-[#2E3A6F]" />
              Products by Category
            </h3>
            {analytics?.category_products?.length > 0 ? (
              <HorizontalBar items={analytics.category_products} />
            ) : (
              <div className="h-36 flex items-center justify-center text-gray-400 text-sm">No data</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-[#2E3A6F]" />
              Most Enquired Products
            </h3>
            {analytics?.top_products?.length > 0 ? (
              <HorizontalBar items={analytics.top_products} />
            ) : (
              <div className="h-36 flex items-center justify-center text-gray-400 text-sm">No enquiry data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Enquiries Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail size={18} className="text-[#2E3A6F]" />
            Recent Enquiries
          </h3>
          {analytics?.recent_enquiries?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recent-enquiries-table">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium uppercase tracking-wider">Name</th>
                    <th className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium uppercase tracking-wider">Email</th>
                    <th className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium uppercase tracking-wider">Product</th>
                    <th className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium uppercase tracking-wider">Status</th>
                    <th className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.recent_enquiries.map((enq, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="py-2.5 px-3 font-medium text-gray-900">{enq.name || '-'}</td>
                      <td className="py-2.5 px-3 text-gray-500">{enq.email || '-'}</td>
                      <td className="py-2.5 px-3 text-gray-600">{enq.product_name || 'General'}</td>
                      <td className="py-2.5 px-3">
                        <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize" style={{
                          backgroundColor: `${STATUS_COLORS[enq.status] || '#6b7280'}15`,
                          color: STATUS_COLORS[enq.status] || '#6b7280'
                        }}>
                          {enq.status || 'new'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-400 text-xs">{enq.created_at?.split('T')[0] || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-gray-400 text-sm">No enquiries yet</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsAdmin;
